import { useCallback, useEffect, useRef, useState } from 'react';
import type { AudioSession, AudioSessionState } from '../client/types';
import { AudioPlayer, type PlayerStats } from '../audio/player';
import { useClient, useDeviceId } from './provider';

export interface ListenState {
  active: boolean;
  state: AudioSessionState | 'idle';
  stats: PlayerStats | null;
  /** Approx. glass-to-ear delay the UI prints ("640 MS BEHIND LIVE"). */
  behindMs: number;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  toggle: () => Promise<void>;
  error: string | null;
}

/**
 * Browser listen: starts the transport session, attaches the AudioWorklet player. Must be called from a
 * user gesture the first time (iOS Safari AudioContext policy) — wire `toggle` to the Listen button.
 */
export function useListen(id?: string, opts?: { webCodecs?: boolean }): ListenState {
  const client = useClient();
  const deviceId = useDeviceId(id);
  const [state, setState] = useState<AudioSessionState | 'idle'>('idle');
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sess = useRef<AudioSession | null>(null);
  const player = useRef<AudioPlayer | null>(null);
  const webCodecs = opts?.webCodecs ?? (typeof localStorage !== 'undefined' && localStorage.getItem('glyph-webcodecs') === '1');

  const stop = useCallback(async () => {
    const s = sess.current;
    const p = player.current;
    sess.current = null;
    player.current = null;
    await p?.detach();
    await s?.stop();
    setState('idle');
    setStats(null);
  }, []);

  const start = useCallback(async () => {
    if (sess.current) return;
    setError(null);
    try {
      const s = client.listen(deviceId);
      sess.current = s;
      setState(s.state);
      s.onState((st) => {
        setState(st);
        if (st === 'error') setError('Listen session failed');
      });
      const p = new AudioPlayer({ webCodecs });
      player.current = p;
      await p.attach(s, (st) => setStats({ ...st }));
    } catch (e) {
      setError((e as Error).message);
      await stop();
    }
  }, [client, deviceId, stop, webCodecs]);

  useEffect(() => () => void stop(), [stop]);

  const active = state !== 'idle' && state !== 'stopped';
  const behindMs = (stats?.bufferMs ?? 120) + 20 /* opus frame */ + 100 /* encode+network est. */ + 400 /* relay+decode est. */;
  return { active, state, stats, behindMs, start, stop, toggle: () => (active ? stop() : start()), error };
}
